import { Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { createRoot } from 'react-dom/client';
import { writeFile } from 'xlsx';

import { CALL_OUTCOME_LABEL_MAP } from '../constants';
import { secondsToTime } from './date-time-format';

export const downloadFile = (obj, filename) => {
  const url = URL.createObjectURL(obj);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const downloadXLSXFile = (data, filename) => {
  writeFile(data, `${filename}.xlsx`);
};

export const generatePDFFile = async (content, cb) => {
  const container = document.createElement('div');
  Object.assign(container.style, {
    width: '1024px',
    height: '1250px',
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    background: 'white',
  });
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(content);

  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  await document.fonts.ready;

  /*
  INFO: Fix image and text vertical alignment in PDF
   https://stackoverflow.com/questions/74980740/html2canvas-shifting-text-downwards/75010048?utm_source=chatgpt.com 
   */
  const style = document.createElement('style');
  style.textContent = `
    img { display: inline-block !important; vertical-align: middle !important; }
  `;
  document.head.appendChild(style);

  const canvas = await html2canvas(container, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: '#fff',
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'px',
    format: [794, 1000],
  });

  const imgProps = pdf.getImageProperties(imgData);
  const pdfWidth = 794;
  const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

  pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
  cb(pdf);

  style.remove();
  root.unmount();
  container.remove();
};

export const createTranscriptDocument = async recording => {
  const document = new Document({
    creator: 'ProspectIQ',
    title: 'Transcript',
    sections: [
      {
        properties: {
          page: {
            margin: { top: 720, right: 720, bottom: 720, left: 720 },
          },
        },
        children: [
          new Paragraph({
            spacing: { after: 240 },
            heading: HeadingLevel.HEADING_2,
            children: [
              new TextRun({
                text: `Client: ${recording.tenant}`,
                bold: true,
                color: '11113A',
              }),
              new TextRun({ break: 1 }),
              new TextRun({
                text: `Caller Name: ${recording.callerName}`,
                bold: true,
                color: '11113A',
              }),
              new TextRun({ break: 1 }),
              new TextRun({
                text: `Contact Name: ${recording.contactName}`,
                bold: true,
                color: '11113A',
              }),
              new TextRun({ break: 1 }),
              new TextRun({
                text: `Contact Company: ${recording.companyName}`,
                bold: true,
                color: '11113A',
              }),
              new TextRun({ break: 1 }),
              new TextRun({
                text: `Date & Time: ${recording.date}`,
                bold: true,
                color: '11113A',
              }),
              new TextRun({ break: 1 }),
              new TextRun({
                text: `Duration: ${secondsToTime(recording.callDuration)}`,
                bold: true,
                color: '11113A',
              }),
              new TextRun({ break: 1 }),
              new TextRun({
                text: `Outcome: ${recording.callOutcome ? CALL_OUTCOME_LABEL_MAP[recording.callOutcome] : '-'}`,
                bold: true,
                color: '11113A',
              }),
            ],
          }),
          ...JSON.parse(recording.clTranscript).map(({ channel, text }) => {
            const isCaller = channel === '1';
            const role = new TextRun({
              text: isCaller ? 'Caller: ' : 'Prospect: ',
              bold: true,
              color: isCaller ? '1e3a8a' : '6b7280',
            });
            const chunks = String(text ?? '').split('\n');
            const content = [];

            chunks.forEach((text, idx) => {
              content.push(
                new TextRun({
                  text,
                  color: isCaller ? '1e3a8a' : '6b7280',
                }),
              );

              if (idx < chunks.length - 1)
                content.push(new TextRun({ break: 1 }));
            });

            return new Paragraph({ children: [role, ...content] });
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(document);

  return blob;
};

const PDF_WIDTH = 1123;
const PDF_HEIGHT = 794;
const ORIENTATION = 'landscape';
const HEADER_DOM_HEIGHT = 128;
const ROW_DOM_HEIGHT = 49;

const mountContent = content => {
  const container = document.createElement('div');

  Object.assign(container.style, {
    position: 'fixed',
    top: '-99999px',
    left: '-99999px',
    background: 'white',
  });

  document.body.appendChild(container);

  const root = createRoot(container);

  root.render(content);

  return { container, root };
};

const injectStyle = () => {
  const style = document.createElement('style');

  style.textContent = `
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    img {
      display: inline-block !important;
      vertical-align: middle !important;
    }
  `;

  document.head.appendChild(style);

  return style;
};

const elementToCanvas = async container =>
  await html2canvas(container, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#fff',
  });

const createPDF = () =>
  new jsPDF({
    orientation: ORIENTATION,
    unit: 'px',
    format: [PDF_WIDTH, PDF_HEIGHT],
  });

const prepareHeader = (canvas, domToCanvasRatio, canvasToPdfScale) => {
  const headerCanvasHeight = Math.floor(HEADER_DOM_HEIGHT * domToCanvasRatio);
  const headerSlice = document.createElement('canvas');

  headerSlice.width = canvas.width;
  headerSlice.height = headerCanvasHeight;

  const hctx = headerSlice.getContext('2d');

  hctx.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    headerCanvasHeight,
    0,
    0,
    headerSlice.width,
    headerSlice.height,
  );

  return {
    headerImgData: headerSlice.toDataURL('image/png'),
    headerPdfHeight: headerSlice.height * canvasToPdfScale,
  };
};

const paginateCanvasToPdf = (
  pdf,
  canvas,
  domToCanvasRatio,
  canvasToPdfScale,
) => {
  const { headerImgData, headerPdfHeight } = prepareHeader(
    canvas,
    domToCanvasRatio,
    canvasToPdfScale,
  );
  const headerCanvasHeight = Math.floor(HEADER_DOM_HEIGHT * domToCanvasRatio);
  const pageContentHeightPdf = PDF_HEIGHT - headerPdfHeight;
  const pageContentHeightCanvas = Math.floor(
    pageContentHeightPdf / canvasToPdfScale,
  );
  const sliceCanvas = document.createElement('canvas');

  sliceCanvas.width = canvas.width;

  const sliceCtx = sliceCanvas.getContext('2d');
  const fullCanvasHeight = canvas.height;
  const contentStartY = headerCanvasHeight;
  let y = contentStartY;
  const rowHeightCanvas = Math.max(
    1,
    Math.round(ROW_DOM_HEIGHT * domToCanvasRatio),
  );

  const rowsPerPage = Math.max(
    1,
    Math.floor(pageContentHeightCanvas / rowHeightCanvas),
  );

  while (y < fullCanvasHeight) {
    const remainingCanvas = fullCanvasHeight - y;

    const remainingRows = Math.ceil(remainingCanvas / rowHeightCanvas);

    let rowsThisPage = Math.min(rowsPerPage, remainingRows);

    const afterThisPage = remainingCanvas - rowsThisPage * rowHeightCanvas;
    if (afterThisPage > 0 && afterThisPage < rowHeightCanvas * 0.5) {
      rowsThisPage = Math.min(rowsPerPage, remainingRows);
    }

    const sliceHeight = rowsThisPage * rowHeightCanvas;

    if (headerImgData) {
      pdf.addImage(headerImgData, 'PNG', 0, 0, PDF_WIDTH, headerPdfHeight);
    }

    sliceCanvas.height = sliceHeight;
    sliceCtx.clearRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sliceCtx.drawImage(
      canvas,
      0,
      y,
      canvas.width,
      sliceHeight,
      0,
      0,
      sliceCanvas.width,
      sliceCanvas.height,
    );

    const imgData = sliceCanvas.toDataURL('image/png');
    const slicePdfHeight = sliceHeight * canvasToPdfScale;

    pdf.addImage(imgData, 'PNG', 0, headerPdfHeight, PDF_WIDTH, slicePdfHeight);

    y += sliceHeight;

    if (y < fullCanvasHeight) {
      pdf.addPage([PDF_WIDTH, PDF_HEIGHT], ORIENTATION);
    }
  }
};

const addPageCount = pdf => {
  const pageCount = pdf.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(12);
    pdf.setTextColor(100);

    const label = `${i} of ${pageCount}`;
    const textWidth = pdf.getTextWidth(label);
    const x = (PDF_WIDTH - textWidth) / 2;
    const y = PDF_HEIGHT - 20;

    pdf.text(label, x, y);
  }
};

export const saveMultiPagePDFFile = async (content, fileName) => {
  const { container, root } = mountContent(content);

  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  await document.fonts.ready;

  const style = injectStyle();
  const canvas = await elementToCanvas(container);
  const pdf = createPDF();
  const containerRect = container.getBoundingClientRect();
  const domToCanvasRatio = canvas.height / containerRect.height;
  const canvasToPdfScale = PDF_WIDTH / canvas.width;

  paginateCanvasToPdf(pdf, canvas, domToCanvasRatio, canvasToPdfScale);
  addPageCount(pdf);

  pdf.save(fileName);

  style.remove();
  root.unmount();
  container.remove();
};
